import React, { Component } from "react";
import { observer } from "mobx-react";
import "./AdminTrackEditor.scss";
import DatePicker from "react-datepicker";
import AdminTrackEditorState from "./AdminTrackEditorState";
import PopupWindow from "../PopupWindow/PopupWindow";
import MusicTrack from "../MusicTrack/MusicTrack";
import { tagsFlat, allTags } from "@shared/constants";

import "react-datepicker/dist/react-datepicker.css";

/** @typedef {import("@shared/typedefs").Tag} Tag */
/** @typedef {import("@shared/typedefs").License} License */

/**
 * @typedef {Object} AdminTrackEditorProps
 * @property {AdminTrackEditorState} store - The admin track editor state store
 */

/**
 * @typedef {Object} AdminTrackEditorReactState
 * @property {boolean} finishedOpening - Whether the component has finished opening animation
 */

@observer
class AdminTrackEditor extends Component {
  /**
   * @param {AdminTrackEditorProps} props
   */
  constructor(props) {
    super(props);

    this.state = {
      finishedOpening: false
    };
  }

  componentWillUnmount() {
    this.setState({
      finishedOpening: false
    });
  }

  render() {
    let musicName = "";
    let musicVersionSearchTerm = "";
    let isAvailableToLicense = true;
    let trackDisplay = null;
    const tagRows = [];
    let licenseArea = null;
    let qualityScore = 0;

    if (this.props.store.searchState.selectedTrack != null) {
      const selectedTrack = this.props.store.searchState.selectedTrack;
      musicName = selectedTrack.music.name;
      musicVersionSearchTerm = selectedTrack.music.version_search_term;
      isAvailableToLicense = selectedTrack.music.is_available_to_license;
      if (this.state.finishedOpening) {
        trackDisplay = (
          <div>
            <MusicTrack depth={0} store={selectedTrack} dontSetRefs adminView />
          </div>
        );
      }

      // add tags
      let tagRow = [];
      for (let i = 0; i < allTags.length; i += 1) {
        /** @type {boolean} */
        const isTag = i < tagsFlat.length;
        /** @type {Tag} */
        const tag = allTags[i];

        const tagValue = selectedTrack.music[tag.urlsafe_name];

        const numbers = [];
        for (let j = 0; j <= 5; j += 1) {
          numbers.push(
            <span
              className={`AdminTrackEditor-tagging-weights-number${
                j === tagValue
                  ? " AdminTrackEditor-tagging-weights-enabled"
                  : ""
              }`}
              key={j}
              onClick={() =>
                this.props.store.toggleTagValue(selectedTrack, tag, j)
              }
            >
              {j}
            </span>
          );
        }
        qualityScore = selectedTrack.music.quality_score;

        tagRow.push(
          <td
            className={`AdminTrackEditor-tagging-cell${
              isTag ? "" : " AdminTrackEditor-tagging-cell-genre"
            }${tagValue > -1 ? " AdminTrackEditor-tagging-cell-active" : ""}`}
            key={i}
          >
            <div>{tag.tag_name}</div>
            <div className={"AdminTrackEditor-tagging-weights-block"}>
              {numbers}
            </div>
          </td>
        );

        if (tagRow.length === 7 || i === allTags.length - 1) {
          tagRows.push(<tr key={tagRows.length}>{tagRow}</tr>);
          tagRow = [];
        }
      }

      if (isAvailableToLicense) {
        licenseArea = (
          <div className={"AdminTrackEditor-enabled-status"}>
            Enabled.{" "}
            <button
              onClick={() => {
                this.props.store.setEnabled(selectedTrack, false);
              }}
            >
              Disable
            </button>
          </div>
        );
      } else {
        licenseArea = (
          <div className={"AdminTrackEditor-disabled-status"}>
            Disabled.{" "}
            <button
              onClick={() => {
                this.props.store.setEnabled(selectedTrack, true);
              }}
            >
              Enable
            </button>
          </div>
        );
      }
    }

    let pasteTags = null;
    if (this.props.store.copyTagsBuffer != null) {
      pasteTags = (
        <button onClick={this.props.store.pasteTags}>
          Paste tags from {this.props.store.copyTagsBuffer.music.name}
        </button>
      );
    }
    let pasteFromParent = null;
    const parentWithTags = this.props.store.getParentWithTags();
    if (parentWithTags != null) {
      pasteFromParent = (
        <button onClick={this.props.store.pasteTagsFromParent}>
          Paste tags from {parentWithTags.music.name}
        </button>
      );
    }

    // Helper to format dates as M/D/YYYY (handles YYYY-MM-DD, ISO, and .toString() formats)
    const formatDateAsMDY = (dateStr) => {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
      } catch {
        return "";
      }
    };

    const licensesBrief = this.props.store
      .getLicenses()
      .map((license, index) => (
        <span key={index}>
          {index + 1}){license.exclusive ? "Exclusive " : ""} -{license.notes} -
          {license.expiration ? formatDateAsMDY(license.expiration) : ""}
        </span>
      ));

    const licenses = this.props.store
      .getLicenses()
      .map((license, index) => (
        <tr key={index}>
          <td>
            <input
              type={"checkbox"}
              checked={license.exclusive}
              onChange={e => {
                console.log(e.target.checked);
                this.props.store.setLicenseExclusive(index, e.target.checked);
              }}
            />
          </td>
          <td>
            <DatePicker
              selected={license.expiration ? new Date(license.expiration) : null}
              onChange={(date) => {
                this.props.store.setLicenseExpiration(index, date.toISOString());
              }}
            />
          </td>
          <td>
            <input
              type={"text"}
              value={license.notes}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onChange={e => {
                this.props.store.setLicenseNote(index, e.target.value);
              }}
            />
          </td>
          <td>
            <button onClick={() => this.props.store.deleteLicense(index)}>
              Delete
            </button>
          </td>
        </tr>
      ));

    return (
      <PopupWindow
        title={"Track Editor"}
        width={1100}
        height={window.screen.height}
        onBlock={() => {}}
        onClose={this.props.store.close}
        center={"screen"}
        copyStyles
        ref={r => {
          this.props.store.setPopupWindow(r);
          if (this.state.finishedOpening === false) {
            setTimeout(() => {
              this.setState({ finishedOpening: true });
            }, 100);
          }
        }}
      >
        <div className={"AdminTrackEditor"}>
          <div className={"AdminTrackEditor-header"}>
            {trackDisplay}
            <div className={"AdminTrackEditor-copy-control-bar"}>
              <button onClick={this.props.store.copyTags}>Copy tags</button>
              {pasteTags}
              {pasteFromParent}
              <button
                onClick={this.props.store.clearTags}
                ref={c => (this.props.store.taggingButton = c)}
              >
                Clear all tags
              </button>
              <span className={"AdminTrackEditor-License-Info"}>
                {licensesBrief}
              </span>
              <span className={"AdminTrackEditor-Quality-Score"}>
                Quality:
                {qualityScore}
              </span>
            </div>
          </div>
          <div className={"AdminTrackEditor-header-spacer"} />
          <table className={"AdminTrackEditor-table"}>
            <tbody>{tagRows}</tbody>
          </table>
          <div className={"AdminTrackEditor-controls-row"}>
            <div className={"AdminTrackEditor-basic-controls"}>
              <div className={"AdminTrackEditor-section"}>
                <b>Track Metadata:</b>
              </div>
              <div className={"AdminTrackEditor-section"}>
                <input
                  type={"text"}
                  placeholder={musicName}
                  value={this.props.store.renameTo}
                  onChange={e => {
                    this.props.store.setRenameTo(e.target.value);
                  }}
                  onKeyDown={e => {
                    e.stopPropagation();
                  }}
                />
                <button onMouseDown={this.props.store.commitRename}>
                  Rename track
                </button>
              </div>
              <div className={"AdminTrackEditor-section"}>
                <input
                  type={"text"}
                  placeholder={musicVersionSearchTerm}
                  value={this.props.store.siblingGroupTo}
                  onChange={e => {
                    this.props.store.setSiblingGroupTo(e.target.value);
                  }}
                  onKeyDown={e => {
                    e.stopPropagation();
                  }}
                />
                <button onMouseDown={this.props.store.commitSiblingGroup}>
                  Set sibling group
                </button>
              </div>
              <div className={"AdminTrackEditor-section"}>{licenseArea}</div>
              <div className={"AdminTrackEditor-section"}>
                <div>Licenses:</div>
                <table className={"AdminTrackEditor-license-table"}>
                  <thead>
                    <tr>
                      <td>
                        <b>exclusive</b>
                      </td>
                      <td>
                        <b>date</b>
                      </td>
                      <td>
                        <b>notes</b>
                      </td>
                      <td>
                        <b>delete</b>
                      </td>
                    </tr>
                  </thead>
                  <tbody>{licenses}</tbody>
                </table>
                <div>
                  <button onClick={this.props.store.addLicense}>
                    Add license
                  </button>
                </div>
              </div>
            </div>
            <div className={"AdminTrackEditor-danger-zone"}>
              <div className={"AdminTrackEditor-section"}>
                <b>Danger Zone!</b>
              </div>
              <div className={"AdminTrackEditor-section"}>
                Deleting tracks cannot be undone.
              </div>
              <div className={"AdminTrackEditor-section"}>
                {(() => {
                  if (this.props.store.deleting) {
                    return <div>Deleting...</div>;
                  }

                  return (
                    <button
                      onMouseDown={() => this.props.store.deleteSelectedTrack()}
                    >
                      Delete track
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </PopupWindow>
    );
  }
}

export default AdminTrackEditor;
